import type { AppRegion } from '@/entities/region'
import {
  API_BASE_URLS,
  DEFAULT_REGION,
} from '@/shared/config'
import { authPersistence, regionStorage } from '@/shared/lib/storage'

/**
 * Retrieves the user's selected API region from localStorage.
 * Checks the auth store first (set after login), then falls back to the
 * region the user selected in the UI (regionStorage).
 * @returns {AppRegion} The determined API region.
 */
export function getUserRegion(): AppRegion {
  if (typeof window !== 'undefined' && window.localStorage) {
    const authRegion = authPersistence.getState().region
    if (authRegion && authRegion in API_BASE_URLS) {
      return authRegion
    }

    const uiRegion = regionStorage.getRegion()
    if (uiRegion && uiRegion in API_BASE_URLS) {
      return uiRegion
    }
  }
  return DEFAULT_REGION
}

/**
 * Gets the full base URL based on the user's stored region.
 * @returns {string} The base URL.
 */
export function getCurrentApiBaseUrl(): string {
  const region = getUserRegion()
  return API_BASE_URLS[region] || API_BASE_URLS[DEFAULT_REGION]
}

// --- WebSocket URL Generation ---

/**
 * Returns the WebSocket URL for the specified region.
 * Derives the URL from the standard API base URL.
 * @returns The WebSocket URL (wss://...).
 */
export function getWebsocketUrl(): string {
  const region = getUserRegion()
  const httpBaseUrl = API_BASE_URLS[region] || API_BASE_URLS[DEFAULT_REGION]

  if (!httpBaseUrl) {
    console.error(`Cannot determine WebSocket URL: No base URL found for region ${region}`)
    throw new Error('WebSocket URL cannot be determined')
  }

  // Replace http/https with ws/wss and append the standard path
  const wsUrl = `${httpBaseUrl
    .replace(/^http:/, 'ws:')
    .replace(/^https:/, 'wss:')
  }/realtime`

  return wsUrl
}
